var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/account/user/user.schemas.ts
import { z } from "zod";
var userInfoSchema = z.object({
  name: z.string(),
  accountId: z.string(),
  userId: z.string(),
  email: z.string(),
  botId: z.string().nullish(),
  avatar: z.string().nullish(),
  isSuperuser: z.boolean().nullish(),
  isDeveloper: z.boolean().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  adminAccountIds: z.array(z.string()).nullish(),
  leaderAccountIds: z.array(z.string()).nullish(),
  readerAccountIds: z.array(z.string()).nullish(),
  otherAccountIds: z.array(z.string()).nullish(),
  sessionId: z.string().nullish(),
  notificationPreferences: z.record(z.string(), z.boolean()).nullish()
});

// src/action/action.schema.ts
import { z as z13 } from "zod";

// src/action/bot.schema.ts
import { z as z6 } from "zod";

// src/action/shared.action.schema.ts
import { z as z5 } from "zod";

// src/bpmn/code.schema.ts
import { z as z2 } from "zod";
var DynamicType = /* @__PURE__ */ function(DynamicType2) {
  DynamicType2["FormData"] = "TraceFormData";
  DynamicType2["StateData"] = "TraceStateData";
  DynamicType2["Lanes"] = "TraceLanes";
  DynamicType2["Action"] = "TraceAction";
  DynamicType2["GlobalState"] = "TraceGlobalState";
  return DynamicType2;
}({});
var StaticType = /* @__PURE__ */ function(StaticType2) {
  StaticType2["FormFile"] = "FormFile";
  StaticType2["Email"] = "Email";
  StaticType2["URL"] = "URLType";
  StaticType2["ToBeDetermined"] = "ToBeDetermined";
  StaticType2["Date"] = "Date";
  StaticType2["DateTime"] = "DateTime";
  StaticType2["ActionComment"] = "ActionComment";
  return StaticType2;
}({});
var UpdateCodeDTOSchema = z2.object({
  code: z2.string(),
  transpiled_code: z2.string().nullable(),
  isActive: z2.boolean()
}).strict();
var LoadedCodeDataSchema = UpdateCodeDTOSchema.merge(z2.object({
  id: z2.string(),
  createdAt: z2.iso.datetime(),
  updatedAt: z2.iso.datetime(),
  deletedAt: z2.iso.datetime().nullable().optional()
})).strict();

// src/bpmn/dnd.schema.ts
import { z as z3 } from "zod";
var StateSchema = z3.any();
var DNDSchema = z3.object({
  id: z3.string(),
  createdAt: z3.iso.datetime(),
  updatedAt: z3.iso.datetime(),
  deletedAt: z3.iso.datetime().nullable().optional(),
  state: StateSchema
});

// src/other/traceKey.schema.ts
import { z as z4 } from "zod";
var TraceKeyMode = {
  Follow: "follow",
  Free: "free",
  Locked: "locked"
};
var ModeSchema = z4.enum(TraceKeyMode);
var TraceKeySchema = z4.object({
  value: z4.string(),
  mode: ModeSchema,
  prodValue: z4.string().nullish()
}).strict();

// src/action/shared.action.schema.ts
var BaseActionSchema = z5.object({
  id: z5.string(),
  bpmn_id: z5.string(),
  createdAt: z5.iso.datetime(),
  updatedAt: z5.iso.datetime(),
  deletedAt: z5.iso.datetime().nullable().optional(),
  calculated: z5.unknown(),
  generation_id: z5.number(),
  workflowId: z5.string(),
  uiDndId: z5.string().optional(),
  effectsDndId: z5.string().optional(),
  customCodeId: z5.string().optional(),
  ui: DNDSchema,
  effects: DNDSchema,
  generated_code: z5.null().optional(),
  traceKey: TraceKeySchema,
  customCode: LoadedCodeDataSchema
});
var BaseCreateActionDTO = z5.object({
  bpmn_id: z5.string(),
  workflow_id: z5.string()
});

// src/action/bot.schema.ts
var BotMetadataSchema = z6.object({
  name: z6.string()
});
var BotDTOSchema = z6.object({
  type: z6.literal("BOT"),
  metadata: BotMetadataSchema
});
var BotSchema = BaseActionSchema.merge(BotDTOSchema);
var CreateBotDTO = BaseCreateActionDTO.merge(BotDTOSchema);

// src/action/eSignature.schema.ts
import { z as z7 } from "zod";
var ESignatureMetadataSchema = z7.object({
  name: z7.string()
});
var ESignatureDTOSchema = z7.object({
  type: z7.literal("E_SIGNATURE"),
  metadata: ESignatureMetadataSchema
});
var ESignatureSchema = BaseActionSchema.merge(ESignatureDTOSchema);
var CreateESignatureDTO = BaseCreateActionDTO.extend(ESignatureDTOSchema);

// src/action/form.schema.ts
import { z as z8 } from "zod";
var FormMetadataSchema = z8.object({
  name: z8.string()
});
var FormDTOSchema = z8.object({
  type: z8.literal("FORM"),
  metadata: FormMetadataSchema
});
var FormSchema = BaseActionSchema.merge(FormDTOSchema);
var CreateFormDTO = BaseCreateActionDTO.merge(FormDTOSchema);

// src/action/tableEditor.schema.ts
import { z as z11 } from "zod";

// src/other/state-menu.schema.ts
import { z as z9 } from "zod";
var AtomReferenceSchema = z9.object({
  dataItemId: z9.string(),
  blockType: z9.string(),
  sourceId: z9.string()
}).strict();

// src/trace/table/table.schema.ts
import { z as z10 } from "zod";
var SettingsSchema = z10.object({
  dataSelectorPathReference: AtomReferenceSchema.optional(),
  allowColumnsSelection: z10.boolean(),
  showEmptyTable: z10.boolean(),
  sortSetup: z10.object({
    columnReference: AtomReferenceSchema.optional(),
    direction: z10.enum([
      "ASC",
      "DESC"
    ]).optional()
  }).optional()
});

// src/action/tableEditor.schema.ts
var TableEditorMetadataSchema = z11.object({
  name: z11.string(),
  settings: SettingsSchema.extend({
    dataPathRef: AtomReferenceSchema.optional(),
    addInitialData: z11.boolean(),
    initialDataKey: z11.string(),
    modifiedDataKey: z11.string(),
    addComments: z11.boolean(),
    commentKey: z11.string()
  })
});
var TableEditorDTOSchema = z11.object({
  type: z11.literal("TABLE_EDITOR"),
  metadata: TableEditorMetadataSchema
});
var TableEditorSchema = BaseActionSchema.extend(TableEditorDTOSchema);
var CreateTableEditorDTO = BaseCreateActionDTO.extend(TableEditorDTOSchema);

// src/action/tableImporter.schema.ts
import { z as z12 } from "zod";
var encodingOptions = [
  "latin1",
  "utf8",
  "ucs2",
  "utf16le",
  "ascii",
  "base64",
  "hex"
];
var EncodingSchema = z12.enum(encodingOptions);
var ParsingSchema = z12.object({
  encoding: EncodingSchema,
  csvParser: z12.object({
    delimiter: z12.string()
  })
});
var TableImporterMetadataSchema = z12.object({
  name: z12.string(),
  settings: SettingsSchema.extend({
    parsing: ParsingSchema,
    addFile: z12.boolean(),
    fileKey: z12.string(),
    addComments: z12.boolean(),
    commentKey: z12.string(),
    aggregation: z12.object({})
  })
});
var TableImporterDTOSchema = z12.object({
  type: z12.literal("TABLE_IMPORTER"),
  metadata: TableImporterMetadataSchema
});
var TableImporterSchema = BaseActionSchema.extend(TableImporterDTOSchema);
var CreateTableImporterDTO = BaseCreateActionDTO.extend(TableImporterDTOSchema);

// src/action/action.schema.ts
var ActionSchema = z13.union([
  FormSchema,
  TableImporterSchema,
  TableEditorSchema
]);
var CreateActionDTOSchema = z13.union([
  CreateFormDTO,
  CreateTableImporterDTO,
  CreateTableEditorDTO,
  CreateBotDTO,
  CreateESignatureDTO
]);
var BpmnTaskSchema = z13.enum([
  "trace:Form",
  "trace:TableEditor",
  "trace:TableImporter",
  "trace:ESignature"
]);
var BPMNTaskToActionType = {
  "trace:Form": "FORM",
  "trace:TableEditor": "TABLE_EDITOR",
  "trace:TableImporter": "TABLE_IMPORTER",
  "trace:ESignature": "E_SIGNATURE"
};
var isBpmnTask = /* @__PURE__ */ __name((task) => {
  return BpmnTaskSchema.safeParse(task).success;
}, "isBpmnTask");

// src/action/types.ts
import { z as z14 } from "zod";
var ActionTypeSchema = z14.enum([
  "FORM",
  "TABLE_IMPORTER",
  "TABLE_EDITOR",
  "E_SIGNATURE",
  "BOT"
]);

// src/atom/variables.ts
import { z as z15 } from "zod";
var ResolvedVariableType = /* @__PURE__ */ function(ResolvedVariableType2) {
  ResolvedVariableType2["Text"] = "Text";
  ResolvedVariableType2["Number"] = "Number";
  ResolvedVariableType2["Boolean"] = "Boolean";
  ResolvedVariableType2["Date"] = "Date";
  ResolvedVariableType2["User"] = "User";
  ResolvedVariableType2["File"] = "File";
  ResolvedVariableType2["List"] = "List";
  ResolvedVariableType2["Comment"] = "Comment";
  ResolvedVariableType2["Custom"] = "Custom";
  ResolvedVariableType2["Table"] = "Table";
  ResolvedVariableType2["Row"] = "Row";
  return ResolvedVariableType2;
}({});
var VariableInfoSchema = z15.object({
  resolvedType: z15.enum(ResolvedVariableType)
});

// src/atom/dataSelector/selectedDatabase.atom.ts
import { z as z16 } from "zod";
var SelectedDatabaseSchema = z16.object({
  selectedDatabase: z16.string().optional()
});

// src/atom/dataSelector/date-selector.atom.ts
import { z as z20 } from "zod";

// src/atom/dataSelector/data-options.atom.ts
import { z as z17 } from "zod";
var DataSourceOptionSchema = z17.enum([
  "normalSource",
  "database",
  "stateVariable"
]);
var SOURCE_INFO_MAP = {
  normalSource: {
    name: "Static Source",
    value: "normalSource"
  },
  database: {
    name: "Database",
    value: "database"
  },
  stateVariable: {
    name: "State Variable",
    value: "stateVariable"
  }
};

// src/atom/dataSelector/data-static.atom.ts
import { z as z19 } from "zod";

// src/other/otherUtils.ts
import { z as z18 } from "zod";
var MAX_TEXT_LENGTH = 250;
var MIN_TITLE_LENGTH = 2;
var TextInputSchema = z18.string().max(MAX_TEXT_LENGTH, {
  message: `Value should be less than ${MAX_TEXT_LENGTH} characters long`
});
var TitleInputSchema = TextInputSchema.min(MIN_TITLE_LENGTH, {
  message: `Value must be at least ${MIN_TITLE_LENGTH} characters long`
});
var DndKitValueSchema = z18.object({
  id: z18.string().or(z18.number()),
  name: z18.string()
}).strict();
var DndKitValuesSchema = z18.array(DndKitValueSchema);

// src/atom/dataSelector/data-static.atom.ts
var NormalSourceSchema = z19.object({
  values: DndKitValuesSchema
});

// src/atom/dataSelector/date-selector.atom.ts
var SourcesDataSchema = z20.object({
  normalSource: NormalSourceSchema.optional(),
  database: SelectedDatabaseSchema.optional(),
  stateVariable: AtomReferenceSchema.optional()
});
var DataSelectorSchema = z20.object({
  dataSource: DataSourceOptionSchema,
  dataSourceData: SourcesDataSchema
});

// src/atom/dataSelector/data-date.atom.ts
import { z as z21 } from "zod";
(function(DateFormatUtils2) {
  DateFormatUtils2.ClassicFormats = [
    "DD/MM/YYYY",
    "MM/DD/YYYY",
    "YYYY/MM/DD"
  ];
  DateFormatUtils2.VerboseFormats = [
    "DD MMM, YYYY",
    "DD MMMM, YYYY",
    "MMM DD, YYYY",
    "MMMM DD, YYYY",
    "ddd, MMM DD, YYYY",
    "dddd, MMMM DD, YYYY"
  ];
  DateFormatUtils2.OtherFormats = [
    "YYYY [Q]Q",
    "[Q]Q YYYY",
    "[Q]Q",
    "YYYY-MM",
    "YYYY"
  ];
  DateFormatUtils2.AllFormats = [
    ...DateFormatUtils2.ClassicFormats,
    ...DateFormatUtils2.VerboseFormats,
    ...DateFormatUtils2.OtherFormats
  ];
})(DateFormatUtils || (DateFormatUtils = {}));
var DateFormatSchema = z21.enum(DateFormatUtils.AllFormats);
var DateFormatUtils;

// src/atom/sources/index.ts
import { z as z90 } from "zod";

// src/atom/sources/dnds/condition/codeCondition.atom.ts
import { z as z22 } from "zod";
var CodeConditionDataSchema = z22.object({
  code: z22.string(),
  isActive: z22.boolean()
});

// src/atom/sources/dnds/condition/condition.atom.ts
import { z as z23 } from "zod";
var OperatorSchema = z23.enum([
  "==",
  "!=",
  ">",
  "<",
  ">=",
  "<=",
  "isTrue",
  "isFalse",
  "contains",
  "defined",
  "startsWith",
  "endsWith",
  "oneOf",
  "allOf",
  "noneOf"
]);
var ConditionInfoSchema = z23.object({
  operator: OperatorSchema,
  value: z23.union([
    z23.string(),
    z23.array(z23.string()),
    z23.null()
  ])
}).strict();
var ConditionDataSchema = z23.object({
  selectedVariable: AtomReferenceSchema.nullable(),
  condition: ConditionInfoSchema.nullable()
}).strict();

// src/atom/sources/dnds/condition/types.ts
var SuggestionSource = /* @__PURE__ */ function(SuggestionSource2) {
  SuggestionSource2["Database"] = "database";
  SuggestionSource2["Variable"] = "variable";
  return SuggestionSource2;
}({});

// src/atom/sources/dnds/condition/condition.library.schema.ts
import { z as z24 } from "zod";
var ConditionSchemas = [
  z24.object({
    type: z24.literal("Condition"),
    data: ConditionDataSchema
  }),
  z24.object({
    type: z24.literal("Code Condition"),
    data: CodeConditionDataSchema
  }),
  z24.object({
    type: z24.literal("Or"),
    data: z24.object({})
  }),
  z24.object({
    type: z24.literal("And"),
    data: z24.object({})
  }),
  z24.object({
    type: z24.literal("Not"),
    data: z24.object({})
  })
];
var CONDITION_TYPES = ConditionSchemas.reduce((acc, schema) => {
  acc.push(...Array.from(schema.shape.type.values));
  return acc;
}, []);

// src/atom/sources/dnds/formBuilder/checkbox.atom.ts
import { z as z26 } from "zod";

// src/atom/sources/dnds/formBuilder/prefill.schema.ts
import { z as z25 } from "zod";
var PrefillDataSchema = z25.object({
  prefill: AtomReferenceSchema.optional()
});

// src/atom/sources/dnds/formBuilder/checkbox.atom.ts
var CheckBoxTypeSchema = z26.enum([
  "checkbox",
  "radio"
]);
var CheckBoxDataSchema = /* @__PURE__ */ __name((valueValidation) => z26.object({
  title: valueValidation ? TitleInputSchema : z26.string(),
  required: z26.boolean(),
  checkboxType: CheckBoxTypeSchema
}).extend(DataSelectorSchema).extend(PrefillDataSchema).strict(), "CheckBoxDataSchema");

// src/atom/sources/dnds/formBuilder/comment.atom.ts
import { z as z27 } from "zod";
var CommentDataSchema = /* @__PURE__ */ __name((valueValidation) => z27.object({
  title: valueValidation ? TitleInputSchema : z27.string(),
  required: z27.boolean(),
  placeholder: valueValidation ? TextInputSchema : z27.string(),
  withMention: z27.boolean()
}).extend(PrefillDataSchema).strict(), "CommentDataSchema");

// src/atom/sources/dnds/formBuilder/date.atom.ts
import { z as z28 } from "zod";
var DateFieldDataSchema = /* @__PURE__ */ __name((valueValidation) => z28.object({
  title: valueValidation ? TitleInputSchema : z28.string(),
  required: z28.boolean()
}).extend(PrefillDataSchema).strict(), "DateFieldDataSchema");

// src/atom/sources/dnds/formBuilder/dropdown.atom.ts
import { z as z29 } from "zod";
var DropDownDataSchema = /* @__PURE__ */ __name((valueValidation) => z29.object({
  title: valueValidation ? TitleInputSchema : z29.string(),
  required: z29.boolean()
}).extend(DataSelectorSchema).extend(PrefillDataSchema).strict(), "DropDownDataSchema");

// src/atom/sources/dnds/formBuilder/file-upload.atom.ts
import { z as z30 } from "zod";
var FileUploadDataSchema = /* @__PURE__ */ __name((valueValidation) => z30.object({
  title: valueValidation ? TitleInputSchema : z30.string(),
  required: z30.boolean()
}).extend(PrefillDataSchema).strict(), "FileUploadDataSchema");

// src/atom/sources/dnds/formBuilder/number.atom.ts
import { z as z31 } from "zod";
var NumberFieldDataSchema = /* @__PURE__ */ __name((valueValidation) => z31.object({
  title: valueValidation ? TextInputSchema : z31.string(),
  required: z31.boolean(),
  placeholder: valueValidation ? TextInputSchema : z31.string(),
  type: z31.enum([
    "number",
    "integer"
  ])
}).extend(PrefillDataSchema).strict(), "NumberFieldDataSchema");

// src/atom/sources/dnds/formBuilder/rich-text.atom.ts
import { z as z32 } from "zod";
var RichTextDataSchema = /* @__PURE__ */ __name((valueValidation) => z32.object({
  title: valueValidation ? TitleInputSchema : z32.string(),
  required: z32.boolean(),
  placeholder: valueValidation ? TextInputSchema : z32.string(),
  withMention: z32.boolean()
}).extend(PrefillDataSchema).strict(), "RichTextDataSchema");

// src/atom/sources/dnds/formBuilder/text-field.atom.ts
import { z as z33 } from "zod";
var TextFieldDataSchema = /* @__PURE__ */ __name((valueValidation) => z33.object({
  title: valueValidation ? TitleInputSchema : z33.string(),
  required: z33.boolean(),
  placeholder: valueValidation ? TextInputSchema : z33.string()
}).extend(PrefillDataSchema).strict(), "TextFieldDataSchema");

// src/atom/sources/dnds/formBuilder/formBuilder.library.schema.ts
import { z as z34 } from "zod";
var FBSchemas = [
  z34.object({
    type: z34.literal("Check Box"),
    data: CheckBoxDataSchema()
  }),
  z34.object({
    type: z34.literal("Date"),
    data: DateFieldDataSchema()
  }),
  z34.object({
    type: z34.literal("Drop Down"),
    data: DropDownDataSchema()
  }),
  z34.object({
    type: z34.literal("File Upload"),
    data: FileUploadDataSchema()
  }),
  z34.object({
    type: z34.literal("Number"),
    data: NumberFieldDataSchema()
  }),
  z34.object({
    type: z34.literal("Rich Text"),
    data: RichTextDataSchema()
  }),
  z34.object({
    type: z34.literal("TextField"),
    data: TextFieldDataSchema()
  }),
  z34.object({
    type: z34.literal("Comment"),
    data: CommentDataSchema()
  })
];
var FB_TYPES = FBSchemas.reduce((acc, schema) => {
  acc.push(...Array.from(schema.shape.type.values));
  return acc;
}, []);

// src/atom/sources/dnds/globalVariable/globalVariable.atom.ts
import { z as z38 } from "zod";

// src/bpmn/bpmn.schema.ts
import { z as z35 } from "zod";
var SingleOrArrayOfString = z35.union([
  z35.string(),
  z35.array(z35.string())
]);
function toArray(el) {
  return Array.isArray(el) ? el : [
    el
  ];
}
__name(toArray, "toArray");
var BPMNCollaborationSchema = z35.object({
  participant: z35.object({
    id: z35.string(),
    name: z35.string(),
    processRef: z35.string()
  }),
  id: z35.string()
});
var BPMNLaneSchema = z35.object({
  flowNodeRef: SingleOrArrayOfString,
  id: z35.string(),
  name: z35.string()
}).transform((val) => {
  return {
    ...val,
    flowNodeRef: toArray(val.flowNodeRef)
  };
});
var BPMNLaneSetSchema = z35.object({
  id: z35.string(),
  lane: z35.array(BPMNLaneSchema)
});
var BPMNSequenceFlowSchema = z35.object({
  id: z35.string(),
  sourceRef: z35.string(),
  targetRef: z35.string()
}).transform((el) => ({
  ...el,
  type: "sequenceFlow"
}));
var BPMNStartEventSchema = z35.object({
  outgoing: SingleOrArrayOfString,
  id: z35.string()
}).transform((val) => {
  return {
    outgoing: toArray(val.outgoing),
    id: val.id,
    type: "start_gateway"
  };
});
var BPMNEndEventSchema = z35.object({
  incoming: SingleOrArrayOfString,
  id: z35.string()
}).transform((val) => ({
  ...val,
  incoming: toArray(val.incoming),
  type: "end_gateway"
}));
var BPMNTaskSchema = z35.object({
  incoming: SingleOrArrayOfString,
  outgoing: SingleOrArrayOfString.optional(),
  id: z35.string(),
  name: z35.string()
}).transform((val) => ({
  ...val,
  incoming: toArray(val.incoming),
  outgoing: toArray(val.outgoing),
  type: "task"
}));
var BPMNParallelGatewaySchema = z35.object({
  incoming: SingleOrArrayOfString,
  outgoing: SingleOrArrayOfString,
  id: z35.string()
}).transform((val) => ({
  ...val,
  incoming: toArray(val.incoming),
  outgoing: toArray(val.outgoing),
  type: "parallel_gateway"
}));
var BPMNExclusiveGatewaySchema = z35.object({
  incoming: SingleOrArrayOfString,
  outgoing: SingleOrArrayOfString,
  id: z35.string()
}).transform((val) => ({
  ...val,
  incoming: toArray(val.incoming),
  outgoing: toArray(val.outgoing),
  type: "exclusive_gateway"
}));
var BPMNInclusiveGatewaySchema = z35.object({
  incoming: SingleOrArrayOfString,
  outgoing: SingleOrArrayOfString,
  id: z35.string()
}).transform((val) => ({
  ...val,
  incoming: toArray(val.incoming),
  outgoing: toArray(val.outgoing),
  type: "inclusive_gateway"
}));
var AllElementSchema = z35.union([
  BPMNStartEventSchema,
  BPMNEndEventSchema,
  BPMNExclusiveGatewaySchema,
  BPMNParallelGatewaySchema,
  BPMNInclusiveGatewaySchema,
  BPMNTaskSchema,
  BPMNSequenceFlowSchema
]);
var BPMNProcessSchema = z35.object({
  laneSet: BPMNLaneSetSchema,
  sequenceFlow: z35.array(BPMNSequenceFlowSchema).or(BPMNSequenceFlowSchema),
  startEvent: z35.array(BPMNStartEventSchema).or(BPMNStartEventSchema),
  // form action
  task: z35.array(BPMNTaskSchema).or(BPMNTaskSchema),
  // form action
  sendTask: z35.array(BPMNTaskSchema).or(BPMNTaskSchema),
  // form action
  serviceTask: z35.array(BPMNTaskSchema).or(BPMNTaskSchema),
  parallelGateway: z35.array(BPMNParallelGatewaySchema).or(BPMNParallelGatewaySchema).optional(),
  endEvent: z35.array(BPMNEndEventSchema).or(BPMNEndEventSchema).optional(),
  exclusiveGateway: z35.array(BPMNExclusiveGatewaySchema).or(BPMNExclusiveGatewaySchema).optional(),
  inclusiveGateway: z35.array(BPMNInclusiveGatewaySchema).or(BPMNInclusiveGatewaySchema).optional()
}).transform((val) => ({
  ...val,
  sequenceFlow: toArray(val.sequenceFlow),
  task: [
    ...toArray(val.task),
    ...toArray(val.sendTask),
    ...toArray(val.serviceTask)
  ],
  parallelGateway: toArray(val.parallelGateway || []),
  endEvent: toArray(val.endEvent || []),
  startEvent: toArray(val.startEvent || []),
  exclusiveGateway: toArray(val.exclusiveGateway || []),
  inclusiveGateway: toArray(val.inclusiveGateway || [])
}));
var BPMNGeneralSchema = z35.object({
  definitions: z35.object({
    collaboration: BPMNCollaborationSchema,
    process: BPMNProcessSchema
  })
});

// src/bpmn/event.schema.ts
import { z as z36 } from "zod";
var EventTypeSchema = z36.enum([
  "START",
  "END",
  "LINK",
  "CREATE_LINK"
]);
var EventSchema = z36.object({
  type: EventTypeSchema,
  bpmn_id: z36.string(),
  id: z36.string(),
  createdAt: z36.iso.datetime(),
  updatedAt: z36.iso.datetime(),
  deletedAt: z36.iso.datetime().nullable().optional()
});

// src/bpmn/gateway.schema.ts
import { z as z37 } from "zod";
var GatewayTypeSchema = z37.enum([
  "PARALLEL",
  "INCLUSIVE",
  "EXCLUSIVE"
]);
var GatewaySchema = z37.object({
  type: GatewayTypeSchema,
  deletedAt: z37.iso.datetime().nullable().optional(),
  id: z37.string()
});

// src/atom/sources/dnds/globalVariable/globalVariable.atom.ts
var ALL_TYPE_KIND = [
  "normal",
  "list",
  "custom"
];
var PRIMARY_TYPES = [
  "string",
  "number",
  "boolean",
  StaticType.Email,
  StaticType.URL,
  StaticType.FormFile
];
var VariableTypeSchema = z38.object({
  selectedKind: z38.enum(ALL_TYPE_KIND),
  type: z38.enum(PRIMARY_TYPES),
  customType: z38.string()
});
var GlobalVariableDataSchema = /* @__PURE__ */ __name((valueValidation) => z38.object({
  title: valueValidation ? TitleInputSchema : z38.string(),
  variableType: VariableTypeSchema,
  defaultValue: z38.any().optional(),
  description: z38.string().optional()
}).strict(), "GlobalVariableDataSchema");

// src/atom/sources/dnds/globalVariable/globalVariable.library.schema.ts
import { z as z39 } from "zod";
var GlobalVariablesSchemas = [
  z39.object({
    type: z39.literal("GlobalVariable"),
    data: GlobalVariableDataSchema()
  })
];
var GLOBAL_VARIABLE_TYPES = GlobalVariablesSchemas.reduce((acc, schema) => {
  acc.push(...Array.from(schema.shape.type.values));
  return acc;
}, []);

// src/atom/sources/dnds/notification/email.atom.ts
import { z as z40 } from "zod";
var DataSourceSchema = z40.object({
  template: z40.string(),
  selectedDestination: z40.string()
}).strict();
var TemplateVariablesSchema = z40.object({
  variable: z40.string(),
  value: AtomReferenceSchema.nullable()
}).strict();
var DataSourceDataSchema = z40.object({
  templateVariables: z40.array(TemplateVariablesSchema).nullable(),
  to: z40.union([
    z40.string(),
    AtomReferenceSchema
  ]).nullable()
}).strict();
var EmailDataSchema = z40.object({
  dataSource: DataSourceSchema.optional(),
  dataSourceData: DataSourceDataSchema.optional()
}).strict();

// src/atom/sources/dnds/notification/notification.library.schema.ts
import { z as z41 } from "zod";
var NotificationSchemas = [
  z41.object({
    type: z41.literal("Send Email"),
    data: EmailDataSchema
  })
];
var NOTIFICATION_TYPES = NotificationSchemas.reduce((acc, schema) => {
  acc.push(...Array.from(schema.shape.type.values));
  return acc;
}, []);

// src/atom/sources/dnds/tableEditor/boolean.atom.ts
import { z as z53 } from "zod";

// src/trace/widget.schema.ts
import { z as z49 } from "zod";

// src/trace/widget/boolean.atom.ts
import { z as z42 } from "zod";
var BooleanMainSchema = z42.object({
  type: z42.literal("boolean")
});

// src/trace/widget/date.atom.ts
import { z as z43 } from "zod";
var DateMainSchema = z43.object({
  type: z43.literal("date"),
  format: DateFormatSchema,
  inputFormat: DateFormatSchema.optional(),
  isDeadline: z43.boolean(),
  deadlineWarningBuffer: z43.number().optional(),
  doneVariableReference: AtomReferenceSchema.optional(),
  deadlineWarningIcon: z43.string().optional()
});

// src/trace/widget/label.atom.ts
import { z as z44 } from "zod";
var LabelMainSchema = z44.object({
  type: z44.literal("labels")
});

// src/trace/widget/number.atom.ts
import { z as z46 } from "zod";

// src/trace/widget/utils.ts
import { z as z45 } from "zod";
var PositiveIntegerSchema = z45.number().int().gte(0);
var LocaleList = [
  "en-US",
  "fr-FR",
  "it-IT"
];
var LocaleSchema = z45.enum(LocaleList);

// src/trace/widget/number.atom.ts
var StyleSchema = z46.enum([
  "decimal",
  "percent",
  "currency"
]);
var CurrencyDisplaySchema = z46.enum([
  "symbol",
  "code",
  "name"
]);
var FormatOptionsSchema = z46.object({
  style: StyleSchema,
  currency: z46.string(),
  currencyDisplay: CurrencyDisplaySchema,
  useGrouping: z46.boolean(),
  minimumFractionDigits: PositiveIntegerSchema,
  maximumFractionDigits: PositiveIntegerSchema,
  minimumIntegerDigits: PositiveIntegerSchema,
  minimumSignificantDigits: PositiveIntegerSchema,
  maximumSignificantDigits: PositiveIntegerSchema
}).partial();
var NumberFormatSchema = z46.object({
  locale: LocaleSchema,
  options: FormatOptionsSchema
});
var NumberMainSchema = z46.object({
  type: z46.literal("number"),
  tabRight: z46.boolean(),
  format: NumberFormatSchema
});

// src/trace/widget/progress.atom.ts
import { z as z47 } from "zod";
var DenominatorTypeList = [
  "none",
  "number",
  "variable"
];
var DenominatorTypeLabelMap = {
  none: "No denominator",
  number: "Static number",
  variable: "State variable"
};
var DenominatorTypeSchema = z47.enum(DenominatorTypeList);
var ProgressMainSchema = z47.object({
  type: z47.literal("progress"),
  pathRef: AtomReferenceSchema.optional(),
  denominator: z47.object({
    type: DenominatorTypeSchema,
    data: z47.object({
      number: z47.number().optional(),
      variableRef: AtomReferenceSchema.optional()
    })
  })
});

// src/trace/widget/text.atom.ts
import { z as z48 } from "zod";
var TextMainSchema = z48.object({
  type: z48.literal("text"),
  default: z48.string().optional(),
  isRichText: z48.boolean().optional(),
  editor: z48.unknown().optional()
});

// src/trace/widget.schema.ts
var WidgetMainSchema = z49.union([
  NumberMainSchema,
  LabelMainSchema,
  DateMainSchema,
  TextMainSchema,
  BooleanMainSchema,
  ProgressMainSchema
]);

// src/trace/cell.schema.ts
import { z as z50 } from "zod";
var MainSchema = z50.object({
  view: WidgetMainSchema
});

// src/trace/table/column.schema.ts
import { z as z52 } from "zod";

// src/trace/table/traceWidthSelector.schema.ts
import { z as z51 } from "zod";
var WidthList = [
  /** 50px */
  "xsmall",
  /** 100px */
  "small",
  /** 200px */
  "medium",
  /** 300px */
  "large",
  /** 500px */
  "xlarge"
];
var WidthToInfoMap = {
  xsmall: {
    name: "Smaller",
    pixel: "50px"
  },
  small: {
    name: "Small",
    pixel: "100px"
  },
  medium: {
    name: "Medium",
    pixel: "200px"
  },
  large: {
    name: "Large",
    pixel: "300px"
  },
  xlarge: {
    name: "Larger",
    pixel: "500px"
  }
};
var TraceWidthSchema = z51.union([
  z51.enum(WidthList),
  PositiveIntegerSchema
]);

// src/trace/table/column.schema.ts
var ColumnMainSchema = /* @__PURE__ */ __name((valueValidation) => z52.object({
  title: valueValidation ? TitleInputSchema : z52.string(),
  width: TraceWidthSchema,
  isfixedColumn: z52.boolean(),
  isDisplayedByDefault: z52.boolean()
}), "ColumnMainSchema");

// src/atom/sources/dnds/tableEditor/boolean.atom.ts
var BooleanColumnDataSchema = /* @__PURE__ */ __name((valueValidation) => z53.object({
  cell: z53.object({
    view: BooleanMainSchema
  }),
  required: z53.boolean()
}).extend(ColumnMainSchema(valueValidation)).strict(), "BooleanColumnDataSchema");

// src/atom/sources/dnds/tableEditor/date.atom.ts
import { z as z55 } from "zod";

// src/atom/sources/dnds/tableEditor/shared.schema.ts
import { z as z54 } from "zod";
var BaseEditorSchema = z54.object({
  placeholder: z54.string().optional(),
  delay: z54.number().optional(),
  disabledPath: AtomReferenceSchema.optional()
});
var EditorTypes = [
  "comment",
  "date",
  "input",
  "multiselect",
  "number",
  "select"
];
var EditorTypeSchema = z54.enum(EditorTypes);
var MultiEditorsSchema = /* @__PURE__ */ __name((editors) => z54.object({
  isEditMode: z54.boolean(),
  editorOptions: z54.object({
    selectedEditor: EditorTypeSchema,
    editors
  })
}).strict(), "MultiEditorsSchema");

// src/atom/sources/dnds/tableEditor/date.atom.ts
var DateEditorSchema = BaseEditorSchema.extend({
  type: z55.literal("date"),
  inputFormat: DateFormatSchema,
  displayFormat: DateFormatSchema,
  saveFormatted: z55.boolean()
});
var DateColumnDataSchema = /* @__PURE__ */ __name((valueValidation) => z55.object({
  cell: z55.object({
    view: DateMainSchema
  }),
  required: z55.boolean()
}).extend(ColumnMainSchema(valueValidation)).extend(MultiEditorsSchema(z55.object({
  date: DateEditorSchema
}))).strict(), "DateColumnDataSchema");

// src/atom/sources/dnds/tableEditor/label.atom.ts
import { z as z56 } from "zod";
var LabelsColumnDataSchema = /* @__PURE__ */ __name((valueValidation) => z56.object({
  cell: z56.object({
    view: WidgetMainSchema
  }),
  required: z56.boolean()
}).extend(ColumnMainSchema(valueValidation)).strict(), "LabelsColumnDataSchema");

// src/atom/sources/dnds/tableEditor/multi-select.atom.ts
import { z as z57 } from "zod";
var MultiSelectEditorSchema = BaseEditorSchema.extend({
  type: z57.literal("multiselect"),
  options: DataSelectorSchema
});
var MultiSelectColumnDataSchema = /* @__PURE__ */ __name((valueValidation) => z57.object({
  required: z57.boolean()
}).extend(ColumnMainSchema(valueValidation)).extend(MultiEditorsSchema(z57.object({
  multiselect: MultiSelectEditorSchema
}))).strict(), "MultiSelectColumnDataSchema");

// src/atom/sources/dnds/tableEditor/number.atom.ts
import { z as z58 } from "zod";
var NumberEditorSchema = BaseEditorSchema.merge(NumberMainSchema.omit({
  tabRight: true
})).extend({
  saveFormatted: z58.boolean()
});
var NumberColumnDataSchema = /* @__PURE__ */ __name((valueValidation) => z58.object({
  cell: z58.object({
    view: NumberMainSchema
  }),
  required: z58.boolean()
}).extend(ColumnMainSchema(valueValidation)).extend(MultiEditorsSchema(z58.object({
  number: NumberEditorSchema
}))).strict(), "NumberColumnDataSchema");

// src/atom/sources/dnds/tableEditor/select.atom.ts
import { z as z59 } from "zod";
var SelectEditorSchema = BaseEditorSchema.extend({
  type: z59.literal("select"),
  options: DataSelectorSchema
});
var SelectColumnDataSchema = /* @__PURE__ */ __name((valueValidation) => z59.object({
  required: z59.boolean()
}).extend(ColumnMainSchema(valueValidation)).extend(MultiEditorsSchema(z59.object({
  select: SelectEditorSchema
}))).strict(), "SelectColumnDataSchema");

// src/atom/sources/dnds/tableEditor/text.atom.ts
import { z as z60 } from "zod";
var InputEditorSchema = BaseEditorSchema.extend({
  type: z60.literal("input")
});
var CommentEditorSchema = BaseEditorSchema.extend({
  type: z60.literal("comment")
});
var TextColumnDataSchema = /* @__PURE__ */ __name((valueValidation) => z60.object({
  cell: z60.object({
    view: TextMainSchema
  }),
  required: z60.boolean()
}).extend(ColumnMainSchema(valueValidation)).extend(MultiEditorsSchema(z60.object({
  input: InputEditorSchema,
  comment: CommentEditorSchema
}))).strict(), "TextColumnDataSchema");

// src/atom/sources/dnds/tableEditor/tableEditor.library.schema.ts
import { z as z61 } from "zod";
var TableEditorSchemas = [
  z61.object({
    type: z61.literal("Table_Editor_Select"),
    data: SelectColumnDataSchema()
  }),
  z61.object({
    type: z61.literal("Table_Editor_Text"),
    data: TextColumnDataSchema()
  }),
  z61.object({
    type: z61.literal("Table_Editor_Number"),
    data: NumberColumnDataSchema()
  }),
  z61.object({
    type: z61.literal("Table_Editor_Date"),
    data: DateColumnDataSchema()
  }),
  z61.object({
    type: z61.literal("Table_Editor_Labels"),
    data: LabelsColumnDataSchema()
  }),
  z61.object({
    type: z61.literal("Table_Editor_Boolean"),
    data: BooleanColumnDataSchema()
  }),
  z61.object({
    type: z61.literal("Table_Editor_MultiSelect"),
    data: MultiSelectColumnDataSchema()
  })
];
var TABLE_EDITOR_TYPES = TableEditorSchemas.reduce((acc, schema) => {
  acc.push(...Array.from(schema.shape.type.values));
  return acc;
}, []);

// src/atom/sources/dnds/tableImporter/boolean.atom.ts
import { z as z63 } from "zod";

// src/atom/sources/dnds/tableImporter/table-importer.atom.ts
import { z as z62 } from "zod";
var ParserSchema = /* @__PURE__ */ __name((parserTypeSchema) => z62.object({
  activated: z62.boolean(),
  parser: parserTypeSchema
}).strict(), "ParserSchema");
var BaseImportOptionsSchema = z62.object({
  from: z62.string()
});
var ImportOptionsSchema = z62.object({
  importerOptions: BaseImportOptionsSchema
}).strict();
var ImportOptionsWithParserSchema = /* @__PURE__ */ __name((parserTypeSchema) => z62.object({
  importerOptions: BaseImportOptionsSchema.extend(z62.object({
    parserOptions: ParserSchema(parserTypeSchema)
  }))
}).strict(), "ImportOptionsWithParserSchema");

// src/atom/sources/dnds/tableImporter/boolean.atom.ts
var TIBooleanColumnDataSchema = /* @__PURE__ */ __name((valueValidation) => z63.object({
  cell: z63.object({
    view: BooleanMainSchema
  }),
  required: z63.boolean()
}).extend(ColumnMainSchema(valueValidation)).extend(ImportOptionsSchema).strict(), "TIBooleanColumnDataSchema");

// src/atom/sources/dnds/tableImporter/date.atom.ts
import { z as z64 } from "zod";
var TIDateColumnDataSchema = /* @__PURE__ */ __name((valueValidation) => z64.object({
  cell: z64.object({
    view: DateMainSchema
  }),
  required: z64.boolean()
}).extend(ColumnMainSchema(valueValidation)).extend(ImportOptionsWithParserSchema(z64.object({
  type: z64.literal("date"),
  inputFormat: DateFormatSchema,
  outputFormat: DateFormatSchema
}))).strict(), "TIDateColumnDataSchema");

// src/atom/sources/dnds/tableImporter/label.atom.ts
import { z as z65 } from "zod";
var TILabelsColumnDataSchema = /* @__PURE__ */ __name((valueValidation) => z65.object({
  cell: z65.object({
    view: WidgetMainSchema
  }),
  required: z65.boolean()
}).extend(ColumnMainSchema(valueValidation)).extend(ImportOptionsSchema).strict(), "TILabelsColumnDataSchema");

// src/atom/sources/dnds/tableImporter/number.atom.ts
import { z as z66 } from "zod";
var TINumberColumnDataSchema = /* @__PURE__ */ __name((valueValidation) => z66.object({
  cell: z66.object({
    view: NumberMainSchema
  }),
  required: z66.boolean()
}).extend(ColumnMainSchema(valueValidation)).extend(ImportOptionsWithParserSchema(z66.object({
  type: z66.literal("number"),
  delimiters: z66.object({
    thousands: z66.string(),
    decimal: z66.string()
  })
}))).strict(), "TINumberColumnDataSchema");

// src/atom/sources/dnds/tableImporter/text.atom.ts
import { z as z67 } from "zod";
var TITextColumnDataSchema = /* @__PURE__ */ __name((valueValidation) => z67.object({
  cell: z67.object({
    view: TextMainSchema
  }),
  required: z67.boolean()
}).extend(ColumnMainSchema(valueValidation)).extend(ImportOptionsSchema).strict(), "TITextColumnDataSchema");

// src/atom/sources/dnds/tableImporter/tableImporter.library.schema.ts
import { z as z68 } from "zod";
var TableImporterSchemas = [
  z68.object({
    type: z68.literal("Table_Importer_Text"),
    data: TITextColumnDataSchema()
  }),
  z68.object({
    type: z68.literal("Table_Importer_Date"),
    data: TIDateColumnDataSchema()
  }),
  z68.object({
    type: z68.literal("Table_Importer_Number"),
    data: TINumberColumnDataSchema()
  }),
  z68.object({
    type: z68.literal("Table_Importer_Labels"),
    data: TILabelsColumnDataSchema()
  }),
  z68.object({
    type: z68.literal("Table_Importer_Boolean"),
    data: TIBooleanColumnDataSchema()
  })
];
var TABLE_IMPORTER_TYPES = TableImporterSchemas.reduce((acc, schema) => {
  acc.push(...Array.from(schema.shape.type.values));
  return acc;
}, []);

// src/atom/sources/dnds/traceInfo/comment.schema.ts
import { z as z69 } from "zod";
var TI_CommentDataSchema = /* @__PURE__ */ __name((_) => z69.object({
  title: z69.string(),
  selectedVariable: AtomReferenceSchema
}).strict(), "TI_CommentDataSchema");

// src/atom/sources/dnds/traceInfo/data-tab.schema.ts
import { z as z70 } from "zod";
var TI_DataTabDataSchema = /* @__PURE__ */ __name((_) => z70.object({
  title: z70.string(),
  selectedVariable: AtomReferenceSchema
}).strict(), "TI_DataTabDataSchema");

// src/atom/sources/dnds/traceInfo/date.schema.ts
import { z as z71 } from "zod";
var TI_DateDataSchema = /* @__PURE__ */ __name((_) => z71.object({
  title: z71.string(),
  selectedVariable: AtomReferenceSchema
}).strict(), "TI_DateDataSchema");

// src/atom/sources/dnds/traceInfo/file-upload.schema.ts
import { z as z72 } from "zod";
var TI_FileUploadDataSchema = /* @__PURE__ */ __name((_) => z72.object({
  title: z72.string(),
  selectedVariable: AtomReferenceSchema
}).strict(), "TI_FileUploadDataSchema");

// src/atom/sources/dnds/traceInfo/key-value.schema.ts
import { z as z73 } from "zod";
var TI_KeyValueDataSchema = /* @__PURE__ */ __name((_) => z73.object({
  title: z73.string(),
  selectedVariable: AtomReferenceSchema
}).strict(), "TI_KeyValueDataSchema");

// src/atom/sources/dnds/traceInfo/list.schema.ts
import { z as z74 } from "zod";
var TI_ListDataSchema = /* @__PURE__ */ __name((_) => z74.object({
  title: z74.string(),
  selectedVariable: AtomReferenceSchema
}).strict(), "TI_ListDataSchema");

// src/atom/sources/dnds/traceInfo/number.schema.ts
import { z as z75 } from "zod";
var TI_NumberDataSchema = /* @__PURE__ */ __name((_) => z75.object({
  title: z75.string(),
  selectedVariable: AtomReferenceSchema
}).strict(), "TI_NumberDataSchema");

// src/atom/sources/dnds/traceInfo/text.schema.ts
import { z as z76 } from "zod";
var TI_TextDataSchema = /* @__PURE__ */ __name((_) => z76.object({
  title: z76.string(),
  selectedVariable: AtomReferenceSchema
}).strict(), "TI_TextDataSchema");

// src/atom/sources/dnds/traceInfo/window.schema.ts
import { z as z77 } from "zod";
var TI_WindowDataSchema = /* @__PURE__ */ __name((valueValidation) => z77.object({
  title: valueValidation ? TitleInputSchema : z77.string(),
  isCollapsable: z77.boolean(),
  value: z77.string()
  //! not sure if this is needed
}).strict(), "TI_WindowDataSchema");

// src/atom/sources/dnds/traceInfo/traceInfo.library.schema.ts
import { z as z78 } from "zod";
var TraceInfoSchemas = [
  z78.object({
    type: z78.literal("TI_Text"),
    data: TI_TextDataSchema()
  }),
  z78.object({
    type: z78.literal("TI_DataTab"),
    data: TI_DataTabDataSchema()
  }),
  z78.object({
    type: z78.literal("TI_Window"),
    data: TI_WindowDataSchema()
  }),
  z78.object({
    type: z78.literal("TI_File"),
    data: TI_FileUploadDataSchema()
  }),
  z78.object({
    type: z78.literal("TI_Date"),
    data: TI_DateDataSchema()
  }),
  z78.object({
    type: z78.literal("TI_List"),
    data: TI_ListDataSchema()
  }),
  z78.object({
    type: z78.literal("TI_Number"),
    data: TI_NumberDataSchema()
  }),
  z78.object({
    type: z78.literal("TI_Comment"),
    data: TI_CommentDataSchema()
  }),
  z78.object({
    type: z78.literal("TI_KeyValue"),
    data: TI_KeyValueDataSchema()
  })
];
var TRACE_INFO_TYPES = TraceInfoSchemas.reduce((acc, schema) => {
  acc.push(...Array.from(schema.shape.type.values));
  return acc;
}, []);

// src/atom/sources/dnds/traceOverview/date.schema.ts
import { z as z80 } from "zod";

// src/atom/atomReference.schema.ts
import { z as z79 } from "zod";
var AtomReferenceSchema2 = z79.object({
  dataItemId: z79.string(),
  blockType: z79.string(),
  sourceId: z79.string()
}).strict();

// src/atom/sources/dnds/traceOverview/date.schema.ts
var TO_DATE_VIEW_TYPES = [
  "Date",
  "Deadline"
];
var TO_DateViewTypeSchema = z80.enum(TO_DATE_VIEW_TYPES);
var TO_DateDataSchema = /* @__PURE__ */ __name((_) => z80.object({
  title: z80.string(),
  selectedVariable: AtomReferenceSchema2,
  viewType: TO_DateViewTypeSchema
}).strict(), "TO_DateDataSchema");

// src/atom/sources/dnds/traceOverview/file-compact.schema.ts
import { z as z81 } from "zod";
var TO_FILE_VIEW_TYPES = [
  "File Compact",
  "File"
];
var TO_FileViewTypeSchema = z81.enum(TO_FILE_VIEW_TYPES);
var TO_FileDataSchema = /* @__PURE__ */ __name((_) => z81.object({
  title: z81.string(),
  selectedVariable: AtomReferenceSchema2,
  viewType: TO_FileViewTypeSchema
}).strict(), "TO_FileDataSchema");

// src/atom/sources/dnds/traceOverview/key-value.schema.ts
import { z as z82 } from "zod";
var TO_KEYVALUE_VIEW_TYPES = [
  "KeyValue"
];
var TO_KeyValueViewTypeSchema = z82.enum(TO_KEYVALUE_VIEW_TYPES);
var TO_KeyValueDataSchema = /* @__PURE__ */ __name((_) => z82.object({
  title: z82.string(),
  selectedVariable: AtomReferenceSchema,
  viewType: TO_KeyValueViewTypeSchema
}).strict(), "TO_KeyValueDataSchema");

// src/atom/sources/dnds/traceOverview/list.schema.ts
import { z as z83 } from "zod";
var TO_LIST_VIEW_TYPES = [
  "List Compact",
  "List"
];
var TO_ListViewTypeSchema = z83.enum(TO_LIST_VIEW_TYPES);
var TO_ListDataSchema = /* @__PURE__ */ __name((_) => z83.object({
  title: z83.string(),
  selectedVariable: AtomReferenceSchema2,
  viewType: TO_ListViewTypeSchema
}).strict(), "TO_ListDataSchema");

// src/atom/sources/dnds/traceOverview/number.schema.ts
import { z as z84 } from "zod";
var TO_NUMBER_VIEW_TYPES = [
  "Number",
  "Progress",
  "Code"
];
var TO_NumberViewTypeSchema = z84.enum(TO_NUMBER_VIEW_TYPES);
var TO_NumberIconSchema = z84.enum([
  "Hash",
  "Loader",
  "Code",
  "FileDigit",
  "CircleDashed"
]);
var TO_NumberDataSchema = /* @__PURE__ */ __name((_) => z84.object({
  title: z84.string(),
  selectedVariable: AtomReferenceSchema2,
  viewType: TO_NumberViewTypeSchema,
  hasIcon: z84.boolean().default(false),
  icon: TO_NumberIconSchema
}).strict(), "TO_NumberDataSchema");

// src/atom/sources/dnds/traceOverview/text.schema.ts
import { z as z85 } from "zod";
var TO_TEXT_VIEW_TYPES = [
  "Text",
  "Code",
  "Link",
  "Prose"
];
var TO_TextViewTypeSchema = z85.enum(TO_TEXT_VIEW_TYPES);
var TO_TextIconSchema = z85.enum([
  "Text",
  "Link",
  "Code",
  "ScrollText",
  "Pilcrow"
]);
var TO_TextDataSchema = /* @__PURE__ */ __name((_) => z85.object({
  title: z85.string(),
  selectedVariable: AtomReferenceSchema2,
  viewType: TO_TextViewTypeSchema,
  hasIcon: z85.boolean().default(false),
  icon: TO_TextIconSchema
}).strict(), "TO_TextDataSchema");

// src/atom/sources/dnds/traceOverview/user.schema.ts
import { z as z86 } from "zod";
var TO_USER_VIEW_TYPES = [
  "Avatar",
  "Activity",
  "Text"
];
var TO_UserViewTypeSchema = z86.enum(TO_USER_VIEW_TYPES);
var TO_UserDataSchema = /* @__PURE__ */ __name((_) => z86.object({
  title: z86.string(),
  selectedVariable: AtomReferenceSchema2,
  viewType: TO_UserViewTypeSchema
}).strict(), "TO_UserDataSchema");

// src/atom/sources/dnds/traceOverview/traceOverview.library.schema.ts
import { z as z87 } from "zod";
var TraceOverviewSchemas = [
  z87.object({
    type: z87.literal("TO_Text"),
    data: TO_TextDataSchema()
  }),
  z87.object({
    type: z87.literal("TO_Date"),
    data: TO_DateDataSchema()
  }),
  z87.object({
    type: z87.literal("TO_Number"),
    data: TO_NumberDataSchema()
  }),
  z87.object({
    type: z87.literal("TO_File"),
    data: TO_FileDataSchema()
  }),
  z87.object({
    type: z87.literal("TO_List"),
    data: TO_ListDataSchema()
  }),
  z87.object({
    type: z87.literal("TO_User"),
    data: TO_UserDataSchema()
  }),
  z87.object({
    type: z87.literal("TO_KeyValue"),
    data: TO_KeyValueDataSchema()
  })
];
var TRACE_OVERVIEW_TYPES = TraceOverviewSchemas.reduce((acc, schema) => {
  acc.push(...Array.from(schema.shape.type.values));
  return acc;
}, []);

// src/atom/sources/dnds/index.ts
var DndAtomSchemas = [
  ...ConditionSchemas,
  ...FBSchemas,
  ...GlobalVariablesSchemas,
  ...NotificationSchemas,
  ...TableEditorSchemas,
  ...TableImporterSchemas,
  ...TraceInfoSchemas,
  ...TraceOverviewSchemas
];
var ALL_BLOCKS = [
  "root",
  ...DndAtomSchemas.map((schema) => schema.shape.type)
];

// src/atom/sources/database/row.atom.ts
import { z as z88 } from "zod";
var CellValuesSchema = z88.union([
  z88.string(),
  z88.number(),
  z88.boolean(),
  z88.date(),
  z88.array(AtomReferenceSchema),
  z88.array(z88.string()),
  z88.null()
]);
var RowDataSchema = z88.object({
  rowAtomId: z88.string()
}).catchall(CellValuesSchema);

// src/atom/sources/database/database.library.schema.ts
import { z as z89 } from "zod";
var DatabaseSchemas = [
  z89.object({
    type: z89.literal("Row"),
    data: RowDataSchema
  })
];
var DATABASE_TYPES = DatabaseSchemas.reduce((acc, schema) => {
  acc.push(...Array.from(schema.shape.type.values));
  return acc;
}, []);

// src/atom/sources/index.ts
var AtomSourceSchema = z90.enum([
  "ui",
  "condition",
  "notification",
  "database",
  "traceInfo",
  "traceOverview",
  "template",
  "globalVariables",
  "variables",
  "transition",
  "form_action",
  "atom",
  "table_editor",
  "table_importer"
]);

// src/atom/atom.schema.ts
import { z as z91 } from "zod";
var MetaInfoSchema = z91.object({
  source: z91.object({
    /**
     * @description the reference of the element where the atom was created
     * @example in a text block of the form editor
     * @returns the id of the element where the atom was created
     */
    elementId: z91.string(),
    /**
     * @description the parent containing the element
     * @example the form library is the parent of the text block
     * @returns the id of the parent containing the element
     */
    parentId: z91.string(),
    /**
     * @description the "kind" of parent
     * @example formEditor, database etc
     */
    parentKind: AtomSourceSchema,
    /**
     * @description a client facing name for the element
     * @example Text Block in Action 5
     */
    name: z91.string().optional()
  }).strict()
}).strict();
var BaseAtomSchema = z91.object({
  /**
  * @description ID of the atom
  */
  id: z91.string(),
  /**
  * @description ID of the process related to the atom
  */
  process_id: z91.string(),
  /**
  * @description key used in the state
  */
  traceKey: TraceKeySchema,
  deletedAt: z91.iso.datetime().nullable().optional(),
  createdAt: z91.iso.datetime().nullable().optional(),
  updatedAt: z91.iso.datetime().nullable().optional(),
  /**
  * @description list of atom ID which reference this atom
  */
  referenced_by: z91.array(z91.string()),
  /**
  * @description list of atom id referencing
  */
  references: z91.array(z91.string()),
  meta_info: MetaInfoSchema.strict(),
  variable_info: VariableInfoSchema.optional().nullable()
});
var dndTypes = DndAtomSchemas.flatMap((schema) => Array.from(schema.shape.type.values));
var dbTypes = DatabaseSchemas.flatMap((schema) => Array.from(schema.shape.type.values));
var ATOM_TYPES = [
  ...dndTypes,
  ...dbTypes
];
var DiscriminatedAtomDataSchema = z91.discriminatedUnion("type", [
  ...DndAtomSchemas,
  ...DatabaseSchemas
]);
var AtomSchema = BaseAtomSchema.and(DiscriminatedAtomDataSchema);

// src/database/database.schema.ts
import { z as z92 } from "zod";
var CellDataType = /* @__PURE__ */ function(CellDataType2) {
  CellDataType2["Text"] = "text";
  CellDataType2["Number"] = "number";
  CellDataType2["Boolean"] = "boolean";
  CellDataType2["Date"] = "date";
  CellDataType2["DateString"] = "dateString";
  CellDataType2["Object"] = "object";
  return CellDataType2;
}({});
var SynchronizeOptionsType = /* @__PURE__ */ function(SynchronizeOptionsType2) {
  SynchronizeOptionsType2["Fetch"] = "FETCH";
  return SynchronizeOptionsType2;
}({});
var SynchronizeDataType = /* @__PURE__ */ function(SynchronizeDataType2) {
  SynchronizeDataType2["TeamMembers"] = "TEAM_MEMBERS";
  SynchronizeDataType2["OrgMembers"] = "ORG_MEMBERS";
  return SynchronizeDataType2;
}({});
var SynchronizeSource = /* @__PURE__ */ function(SynchronizeSource2) {
  SynchronizeSource2["StratumnAccountMembers"] = "STRATUMN_ACCOUNT_MEMBERS";
  return SynchronizeSource2;
}({});
var syncedDataSchema = z92.object({
  source: z92.enum(SynchronizeSource),
  type: z92.enum(SynchronizeDataType),
  options: z92.record(z92.string(), z92.string()).optional()
});
var synchronizeOptionsSchema = z92.object({
  type: z92.enum(SynchronizeOptionsType),
  syncedData: syncedDataSchema
});
var ColumnContextSchema = z92.object({
  reference: z92.object({
    databaseId: z92.string()
  }).optional(),
  isPrimaryKey: z92.boolean().optional(),
  editableAfterGeneration: z92.boolean().default(true).optional(),
  regex: z92.string().optional(),
  synchronizeOptions: synchronizeOptionsSchema.optional(),
  multiSelectOptions: z92.array(z92.string()).optional()
}).nullish();
var DatabaseColumnSchema = z92.object({
  field: z92.string(),
  headerName: z92.string().optional(),
  cellDataType: z92.enum(CellDataType),
  context: ColumnContextSchema
}).loose();
var ColumnsSchema = z92.array(DatabaseColumnSchema);
var DatabaseContextSchema = z92.object({
  rowDeletionDisabled: z92.boolean()
});
var DatabaseSchema = z92.object({
  id: z92.string(),
  name: z92.string(),
  traceKey: TraceKeySchema,
  columnDefinitions: ColumnsSchema,
  rowReferences: z92.array(AtomReferenceSchema2),
  context: DatabaseContextSchema,
  processId: z92.string(),
  createdAt: z92.iso.datetime(),
  updatedAt: z92.iso.datetime(),
  deletedAt: z92.iso.datetime().nullable()
}).strict();
var UpdateDatabaseSchema = DatabaseSchema.omit({
  id: true
});

// src/generation/generation.schema.ts
import { jobExecutionSchema } from "@stratumn/jobs";
import { z as z95 } from "zod";

// src/other/user.schema.ts
import { z as z93 } from "zod";
var UserSchema = z93.object({
  avatar: z93.url().nullish(),
  email: z93.email(),
  firstName: z93.string().optional(),
  lastName: z93.string().optional(),
  isSuperuser: z93.boolean().default(false).optional(),
  isDeveloper: z93.boolean().default(false).optional(),
  name: z93.string(),
  rowId: z93.string(),
  __typename: z93.string().optional()
}).loose();
var hasElevatedRights = /* @__PURE__ */ __name((user) => Boolean(user?.isSuperuser || user?.isDeveloper), "hasElevatedRights");

// src/other/workspace.schema.ts
import { z as z94 } from "zod";
var WORKSPACE_NAME_MIN_LENGTH = 3;
var WORKSPACE_NAME_MAX_LENGTH = 255;
var WORKSPACE_VERSION_GROUPS = [
  "stable",
  "candidate",
  "dev"
];
var WorkspaceVersionSchema = z94.object({
  name: z94.string().min(1),
  displayName: z94.string(),
  type: z94.string().min(1),
  group: z94.enum(WORKSPACE_VERSION_GROUPS),
  createdAt: z94.iso.datetime()
}).strict();
var PublishedWorkflowIdsSchema = z94.record(z94.string(), z94.string());
var WorkspaceSchema = z94.object({
  id: z94.string(),
  name: z94.string(),
  color: z94.string(),
  version: WorkspaceVersionSchema.nullable(),
  tags: z94.record(z94.string(), z94.union([
    z94.string(),
    z94.number(),
    z94.boolean()
  ])),
  processId: z94.string(),
  /**
   * Foreign key (user id) of the workspace creator. Not hydrated; resolve
   * via account-api. Optional + nullable to tolerate legacy rows that
   * predate the column and partial API responses that don't project it.
   */
  createdBy: z94.string().nullish(),
  createdAt: z94.iso.datetime(),
  updatedAt: z94.iso.datetime(),
  published_workflow_ids: PublishedWorkflowIdsSchema.optional()
}).strict();
var workspaceNameSchema = z94.string().trim().min(WORKSPACE_NAME_MIN_LENGTH, `Name must be at least ${String(WORKSPACE_NAME_MIN_LENGTH)} characters`).max(WORKSPACE_NAME_MAX_LENGTH, `Name must be at most ${String(WORKSPACE_NAME_MAX_LENGTH)} characters`);
var HEX_COLOR_REGEX = /^#[\da-fA-F]{6}$/;
var workspaceColorSchema = z94.string().regex(HEX_COLOR_REGEX, "Color must be valid hex format (e.g. #ff0000)").default("#6366f1");
var workspaceTagsSchema = z94.record(z94.string(), z94.union([
  z94.string(),
  z94.number(),
  z94.boolean()
]));
var workspaceVersionFieldSchema = WorkspaceVersionSchema;
var CreateWorkspaceSchema = z94.object({
  name: workspaceNameSchema,
  color: workspaceColorSchema,
  version: WorkspaceVersionSchema.nullable(),
  tags: workspaceTagsSchema.default({})
}).strict();
var UpdateWorkspaceSchema = z94.object({
  name: workspaceNameSchema.optional(),
  color: workspaceColorSchema.optional(),
  version: WorkspaceVersionSchema.nullable().optional(),
  tags: workspaceTagsSchema.optional()
}).strict();
var AdminUpdateWorkspaceSchema = z94.object({
  published_workflow_ids: PublishedWorkflowIdsSchema
}).strict();
var workspaceSettingsFormSchema = z94.object({
  gitlabRepositoryId: z94.number().int(),
  organizationName: z94.string(),
  workspaceActivated: z94.boolean()
});
var workspaceActivationSchema = z94.object({
  gitlabRepositoryId: z94.number().int().positive(),
  organizationName: z94.string().min(2),
  workspaceActivated: z94.literal(true)
});

// src/generation/generation.schema.ts
var HydratedGenerationSchema = z95.object({
  id: z95.string(),
  createdAt: z95.string(),
  createdBy: UserSchema,
  job_id: z95.string().nullable(),
  process_id: z95.string(),
  source: z95.string(),
  jobExecutions: z95.array(jobExecutionSchema),
  workspace_id: z95.string().nullable(),
  workspace_name: z95.string().nullable(),
  workspace_version: WorkspaceVersionSchema.nullable()
});
var GenerationMetadataSchema = z95.object({
  numberOfGenerations: z95.number()
});
var toArray2 = /* @__PURE__ */ __name((val) => {
  if (typeof val === "string") {
    return [
      val
    ];
  }
  return val;
}, "toArray");
var FILTER_OPERATORS = [
  "is",
  "is_not",
  "is_any_of",
  "is_not_any_of"
];
var GetGenerationsParamsSchema = z95.object({
  page: z95.coerce.number().optional(),
  take: z95.coerce.number().optional(),
  workspace_id: z95.preprocess(toArray2, z95.array(z95.string()).optional()),
  workspace_id_op: z95.enum(FILTER_OPERATORS).optional(),
  version_group: z95.preprocess(toArray2, z95.array(z95.enum(WORKSPACE_VERSION_GROUPS)).optional()),
  version_group_op: z95.enum(FILTER_OPERATORS).optional(),
  created_by: z95.preprocess(toArray2, z95.array(z95.string()).optional()),
  created_by_op: z95.enum(FILTER_OPERATORS).optional()
}).refine((params) => {
  if (params.page !== void 0) {
    return params.take !== void 0;
  }
  return true;
}, {
  message: "If 'page' is defined, 'take' must also be defined in query params",
  path: [
    "take"
  ]
});
var UpdateGenerationSchema = z95.object({
  currentlyDeployed: z95.boolean().optional(),
  timedOutAt: z95.string().nullish()
});
var GenerationStatus = /* @__PURE__ */ function(GenerationStatus2) {
  GenerationStatus2["RUNNING"] = "Running";
  GenerationStatus2["FAILED"] = "Failed";
  GenerationStatus2["READY"] = "Ready";
  GenerationStatus2["PENDING"] = "Pending";
  GenerationStatus2["TIMEOUT"] = "Timeout";
  return GenerationStatus2;
}({});
var GENERATION_STATUSES = [
  "PENDING",
  "RUNNING",
  "READY",
  "FAILED",
  "TIMEOUT"
];
var GenerationListItemSchema = z95.object({
  id: z95.string(),
  createdAt: z95.iso.datetime(),
  createdBy: UserSchema,
  source: z95.string(),
  status: z95.enum(GENERATION_STATUSES),
  duration: z95.number().nullable(),
  workspaceId: z95.string().nullable(),
  workspaceName: z95.string().nullable(),
  workspaceVersion: WorkspaceVersionSchema.nullable(),
  isCurrentlyDeployed: z95.boolean()
});
var GenerationDetailSchema = GenerationListItemSchema.extend({
  jobId: z95.string().nullable(),
  processId: z95.string(),
  jobExecutions: z95.array(jobExecutionSchema)
});
var GenerationLogSchema = z95.object({
  timestamp: z95.iso.datetime(),
  level: z95.enum([
    "info",
    "warn",
    "error"
  ]),
  message: z95.string()
});

// src/other/env-var.schema.ts
import { z as z96 } from "zod";
var ENV_VAR_NAME_REGEX = /^[A-Z][A-Z0-9_]*$/;
var RESERVED_ENV_VAR_NAMES = [
  "STAGE",
  "WORKFLOWS",
  "REPOSITORIES",
  "REPOSITORIES_FILE_PATH",
  "WORKSPACE",
  "ORGANIZATION_NAME",
  "MEDIA_API_URL"
];
var RESERVED_NAME_MESSAGE = "Name is reserved by the system";
var isReservedName = /* @__PURE__ */ __name((name) => RESERVED_ENV_VAR_NAMES.some((reserved) => reserved === name), "isReservedName");
var envVarNameSchema = z96.string().regex(ENV_VAR_NAME_REGEX, "Must be UPPER_SNAKE_CASE").refine((name) => !isReservedName(name), {
  message: RESERVED_NAME_MESSAGE
});
var envVarTypeSchema = z96.enum([
  "string",
  "number",
  "boolean"
]);
var envVarValueSchema = /* @__PURE__ */ __name((type) => {
  switch (type) {
    case "boolean":
      return z96.enum([
        "true",
        "false"
      ]);
    case "number":
      return z96.string().regex(/^-?\d+(\.\d+)?$/, "Must be a valid number");
    default:
      return z96.string().min(1, "Value is required");
  }
}, "envVarValueSchema");
var processEnvVarSchema = z96.object({
  id: z96.string(),
  processId: z96.string(),
  name: z96.string().regex(ENV_VAR_NAME_REGEX),
  type: envVarTypeSchema,
  value: z96.string(),
  shared: z96.boolean(),
  enabled: z96.boolean(),
  createdAt: z96.string(),
  updatedAt: z96.string()
});
var createEnvVarSchema = z96.object({
  name: envVarNameSchema,
  type: envVarTypeSchema,
  value: z96.string().min(1, "Default value is required"),
  shared: z96.boolean(),
  enabled: z96.boolean().default(true)
}).superRefine((data, ctx) => {
  const valueResult = envVarValueSchema(data.type).safeParse(data.value);
  if (!valueResult.success) {
    for (const issue of valueResult.error.issues) {
      ctx.addIssue({
        code: "custom",
        message: issue.message,
        path: [
          "value"
        ]
      });
    }
  }
});
var updateEnvVarSchema = z96.object({
  name: envVarNameSchema.optional(),
  type: envVarTypeSchema.optional(),
  value: z96.string().min(1).optional(),
  shared: z96.boolean().optional(),
  enabled: z96.boolean().optional()
});
var envVarOverrideSchema = z96.object({
  id: z96.string(),
  workspaceId: z96.string(),
  envVarId: z96.string(),
  value: z96.string(),
  enabled: z96.boolean(),
  createdAt: z96.string(),
  updatedAt: z96.string(),
  // Optional join field included by `listOverrides` (Prisma `include`). Kept
  // optional so internal callers that don't need the join can still parse.
  envVar: z96.object({
    name: z96.string(),
    type: envVarTypeSchema
  }).optional()
});
var upsertEnvVarOverrideSchema = z96.object({
  envVarId: z96.string(),
  value: z96.string().min(1, "Value is required"),
  enabled: z96.boolean()
});
var bulkUpsertEnvVarOverridesSchema = z96.object({
  overrides: z96.array(upsertEnvVarOverrideSchema)
});

// src/other/notification.schema.ts
import { z as z97 } from "zod";
var TextSchema = z97.array(z97.any());
var NotificationTemplateSchema = z97.object({
  id: z97.string(),
  title: z97.string(),
  text: TextSchema,
  createdAt: z97.string(),
  updatedAt: z97.string(),
  process_id: z97.string()
}).strict();

// src/other/organization-admin.schema.ts
import { z as z98 } from "zod";
var OrganizationTypeSchema = z98.enum([
  "prod",
  "dev",
  "demo",
  "test"
]);
var OrganizationAdminItemSchema = z98.object({
  rowId: z98.coerce.string(),
  name: z98.string(),
  acceptsExternalMembers: z98.boolean(),
  organizationType: OrganizationTypeSchema,
  primaryOrganization: z98.object({
    rowId: z98.coerce.string(),
    name: z98.string()
  }).nullable()
});
var OrganizationAdminListSchema = z98.array(OrganizationAdminItemSchema);
var UpdateOrganizationLinkSchema = z98.object({
  primaryOrganizationRowId: z98.coerce.string().nullable().optional(),
  acceptsExternalMembers: z98.boolean().optional(),
  organizationType: OrganizationTypeSchema.optional()
}).refine((v) => v.primaryOrganizationRowId !== void 0 || v.acceptsExternalMembers !== void 0 || v.organizationType !== void 0, {
  message: "Provide primaryOrganizationRowId, acceptsExternalMembers and/or organizationType"
});
var CreateOrganizationSchema = z98.object({
  name: z98.string().trim().min(1, "Name is required"),
  description: z98.string().trim().optional(),
  organizationType: OrganizationTypeSchema.default("prod")
});

// src/other/process.schema.ts
import { z as z101 } from "zod";

// src/other/transition.schema.ts
import { z as z99 } from "zod";
var CreateTransitionDTOSchema = z99.object({
  processId: z99.string(),
  transitionId: z99.string()
});
var TransitionSchema = z99.object({
  id: z99.string(),
  condition: DNDSchema,
  notification: DNDSchema,
  priorities: DNDSchema,
  conditionDnd: z99.string().optional(),
  notificationDnd: z99.string().optional(),
  prioritiesDnd: z99.string().optional()
});

// src/other/workflow.schema.ts
import z100 from "zod";
var WorkflowMetadataSchema = z100.object({
  name: z100.string().optional(),
  bpmn_js_id: z100.string().optional(),
  id_environment_variable_name: z100.string().optional()
}).strict();
var BaseWorkflowSchema = z100.object({
  id: z100.string(),
  published_id: z100.string().nullable(),
  workflow_xml: z100.string(),
  updatedAt: z100.iso.datetime(),
  metadata: WorkflowMetadataSchema
});
var WorkflowSchema = BaseWorkflowSchema.extend({
  Event: z100.array(EventSchema),
  Action: z100.array(ActionSchema),
  Gateway: z100.array(GatewaySchema),
  globalVariables: DNDSchema,
  traceInfo: DNDSchema,
  traceOverview: DNDSchema
});
var UpdateWorkflowSchema = z100.object({
  metadata: WorkflowMetadataSchema.optional(),
  workflow_xml: z100.string().optional()
});
var AdminUpdateWorkflowSchema = z100.object({
  published_id: z100.string().nullable().optional(),
  id_environment_variable_name: z100.string().optional()
});
var CreateWorkflowBodySchema = z100.object({
  name: z100.string().min(1),
  processId: z100.string().min(1)
});

// src/other/process.schema.ts
var PermissionSchema = z101.object({
  can_edit: z101.boolean(),
  can_view: z101.boolean(),
  can_delete: z101.boolean(),
  can_leave: z101.boolean()
});
var CollaboratorSchema = z101.object({
  name: z101.string(),
  email: z101.string(),
  id: z101.string(),
  permissions: PermissionSchema,
  avatar: z101.string().nullish()
});
var BaseSchema = z101.object({
  id: z101.string(),
  name: z101.string(),
  icon: z101.string(),
  published: z101.boolean(),
  atom: z101.array(AtomSchema),
  permission: PermissionSchema,
  collaborators: z101.array(CollaboratorSchema),
  is_public: z101.boolean(),
  updatedAt: z101.string(),
  createdAt: z101.string(),
  deletedAt: z101.iso.datetime().nullable().optional(),
  image: z101.string().optional().nullable(),
  databases: z101.array(DatabaseSchema),
  gitlabRepositoryId: z101.number(),
  organizationName: z101.string().optional().nullable(),
  tag: z101.string().nullable().optional(),
  workspaceActivated: z101.boolean().default(false)
});
var DraftSchema = BaseSchema.extend({
  draft: z101.literal(true),
  workflows: z101.array(BaseWorkflowSchema)
});
var ProcessSchema = BaseSchema.extend({
  draft: z101.literal(false),
  notificationTemplates: z101.array(NotificationTemplateSchema),
  Transition: z101.array(TransitionSchema),
  workflows: z101.array(WorkflowSchema)
});
var GenericStudioProcessSchema = z101.discriminatedUnion("draft", [
  DraftSchema,
  ProcessSchema
]);
var SmallProcessSchema = z101.object({
  id: z101.string(),
  name: z101.string(),
  updatedAt: z101.string(),
  createdAt: z101.string(),
  published: z101.boolean(),
  icon: z101.string(),
  draft: z101.boolean(),
  permission: PermissionSchema,
  gitlabRepositoryId: z101.number(),
  tag: z101.string().nullable().optional(),
  workspaceActivated: z101.boolean().default(false),
  workflows: z101.array(z101.object({
    id: z101.string(),
    published_id: z101.string().nullable()
  }))
});

// src/repo/repo.schema.ts
import { z as z102 } from "zod";
var relationSchema = z102.object({
  databaseKey: z102.string()
});
var baseColumnSchema = z102.object({
  field: z102.string(),
  name: z102.string().optional().describe("Defaults to field if not provided"),
  headerName: z102.string().optional().describe("Defaults to field if not provided"),
  editableAfterGeneration: z102.boolean().optional(),
  relation: relationSchema.optional(),
  regex: z102.string().optional()
});
var columnSchema = baseColumnSchema.and(z102.discriminatedUnion("type", [
  z102.object({
    type: z102.literal("text"),
    isPrimary: z102.boolean().optional()
  }),
  z102.object({
    type: z102.enum([
      "number",
      "boolean",
      "date",
      "dateString",
      "object"
    ])
  })
]));
var databaseSchema = z102.object({
  key: z102.string(),
  name: z102.string(),
  columns: columnSchema.array(),
  canDeleteRows: z102.boolean().optional()
});
var genRepoSchema = z102.object({
  processId: z102.string(),
  processName: z102.string().optional(),
  databases: databaseSchema.array()
});
var workflowItemSchema = z102.object({
  id: z102.string().min(1, "Workflow ID is required"),
  name: z102.string().min(1, "Workflow name is required"),
  id_environment_variable_name: z102.string().min(1, "Workflow id environment variable name is required").optional()
});
var processConfigSchema = z102.object({
  registryImage: z102.string().regex(/^[\w.:/-]+$/, "Invalid registry image format").optional(),
  workflows: z102.array(workflowItemSchema).optional()
});
var rowData = z102.record(z102.string(), z102.any());
var dataSchema = z102.record(z102.string(), z102.array(rowData));
var createDtoSchema = z102.object({
  schema: genRepoSchema,
  data: dataSchema.optional(),
  process: processConfigSchema.optional()
}).superRefine((val, ctx) => {
  const primaryKeyByDb = {};
  for (const db of val.schema.databases) {
    const primaryCol = db.columns.find((col) => col.type === "text" && col.isPrimary);
    if (!primaryCol) {
      ctx.addIssue({
        code: "custom",
        message: `Database '${db.key}' must have a primary column of type 'text'`,
        path: [
          "schema",
          "databases",
          db.key
        ]
      });
      continue;
    }
    primaryKeyByDb[db.key] = primaryCol.field;
  }
  const definedDbKeys = new Set(val.schema.databases.map((db) => db.key));
  for (const db of val.schema.databases) {
    for (const col of db.columns) {
      if (col.relation && !definedDbKeys.has(col.relation.databaseKey)) {
        ctx.addIssue({
          code: "custom",
          message: `Column '${col.field}' in database '${db.key}' references database key '${col.relation.databaseKey}' which is not defined in the schema databases`,
          path: [
            "schema",
            "databases",
            db.key,
            "columns",
            col.field,
            "relation",
            "databaseKey"
          ]
        });
      }
    }
  }
  if (!val.data) {
    return;
  }
  for (const [dbKey, rows] of Object.entries(val.data)) {
    const dbSchema = val.schema.databases.find((db) => db.key === dbKey);
    if (!dbSchema) {
      ctx.addIssue({
        code: "custom",
        message: `Data contains database key '${dbKey}' which is not defined in the schema databases`,
        path: [
          "data",
          dbKey
        ]
      });
      continue;
    }
    for (const col of dbSchema.columns) {
      if (!col.relation) {
        continue;
      }
      const referencedDbKey = col.relation.databaseKey;
      const referencedPrimaryKey = primaryKeyByDb[referencedDbKey];
      const referencedRows = val.data[referencedDbKey] || [];
      const potentialReferencedValues = new Set(referencedRows.map((row) => row[referencedPrimaryKey]));
      for (const [rowIndex, row] of rows.entries()) {
        const values = row[col.field];
        if (values && !Array.isArray(values)) {
          ctx.addIssue({
            code: "custom",
            message: `In database '${dbKey}', row ${rowIndex}, column '${col.field}': expected an array of strings referencing related database '${referencedDbKey}'`,
            path: [
              "data",
              dbKey,
              rowIndex,
              col.field
            ]
          });
        }
        for (const value of values) {
          if (!potentialReferencedValues.has(value)) {
            ctx.addIssue({
              code: "custom",
              message: `In database '${dbKey}', row ${rowIndex}, column '${col.field}': value '${value}' does not reference any existing row in related database '${referencedDbKey}'`,
              path: [
                "data",
                dbKey,
                rowIndex,
                col.field
              ]
            });
          }
        }
      }
    }
  }
});
export {
  ALL_BLOCKS,
  ALL_TYPE_KIND,
  ATOM_TYPES,
  ActionSchema,
  AdminUpdateWorkflowSchema,
  AdminUpdateWorkspaceSchema,
  AtomReferenceSchema2 as AtomReferenceSchema,
  AtomSchema,
  AtomSourceSchema,
  BPMNCollaborationSchema,
  BPMNEndEventSchema,
  BPMNExclusiveGatewaySchema,
  BPMNGeneralSchema,
  BPMNInclusiveGatewaySchema,
  BPMNLaneSchema,
  BPMNLaneSetSchema,
  BPMNParallelGatewaySchema,
  BPMNProcessSchema,
  BPMNSequenceFlowSchema,
  BPMNStartEventSchema,
  BPMNTaskSchema,
  BPMNTaskToActionType,
  BaseActionSchema,
  BaseCreateActionDTO,
  BaseEditorSchema,
  BaseWorkflowSchema,
  BooleanColumnDataSchema,
  BooleanMainSchema,
  BotDTOSchema,
  BotSchema,
  CONDITION_TYPES,
  CellDataType,
  CheckBoxDataSchema,
  CheckBoxTypeSchema,
  CodeConditionDataSchema,
  ColumnContextSchema,
  ColumnMainSchema,
  ColumnsSchema,
  CommentDataSchema,
  ConditionDataSchema,
  ConditionInfoSchema,
  ConditionSchemas,
  CreateActionDTOSchema,
  CreateBotDTO,
  CreateESignatureDTO,
  CreateFormDTO,
  CreateOrganizationSchema,
  CreateTableEditorDTO,
  CreateTableImporterDTO,
  CreateTransitionDTOSchema,
  CreateWorkflowBodySchema,
  CreateWorkspaceSchema,
  DATABASE_TYPES,
  DNDSchema,
  DataSelectorSchema,
  DataSourceDataSchema,
  DataSourceOptionSchema,
  DataSourceSchema,
  DatabaseColumnSchema,
  DatabaseSchema,
  DatabaseSchemas,
  DateColumnDataSchema,
  DateFieldDataSchema,
  DateFormatSchema,
  DateFormatUtils,
  DateMainSchema,
  DenominatorTypeLabelMap,
  DenominatorTypeList,
  DiscriminatedAtomDataSchema,
  DndAtomSchemas,
  DndKitValueSchema,
  DndKitValuesSchema,
  DraftSchema,
  DropDownDataSchema,
  DynamicType,
  ENV_VAR_NAME_REGEX,
  ESignatureDTOSchema,
  ESignatureSchema,
  EditorTypes,
  EmailDataSchema,
  EventSchema,
  FBSchemas,
  FB_TYPES,
  FILTER_OPERATORS,
  FileUploadDataSchema,
  FormDTOSchema,
  FormSchema,
  GENERATION_STATUSES,
  GLOBAL_VARIABLE_TYPES,
  GatewaySchema,
  GatewayTypeSchema,
  GenerationDetailSchema,
  GenerationListItemSchema,
  GenerationLogSchema,
  GenerationMetadataSchema,
  GenerationStatus,
  GenericStudioProcessSchema,
  GetGenerationsParamsSchema,
  GlobalVariableDataSchema,
  GlobalVariablesSchemas,
  HydratedGenerationSchema,
  LabelMainSchema,
  LabelsColumnDataSchema,
  LoadedCodeDataSchema,
  LocaleList,
  LocaleSchema,
  MainSchema,
  MetaInfoSchema,
  ModeSchema,
  MultiEditorsSchema,
  MultiSelectColumnDataSchema,
  NOTIFICATION_TYPES,
  NormalSourceSchema,
  NotificationSchemas,
  NotificationTemplateSchema,
  NumberColumnDataSchema,
  NumberFieldDataSchema,
  NumberFormatSchema,
  NumberMainSchema,
  OperatorSchema,
  OrganizationAdminItemSchema,
  OrganizationAdminListSchema,
  OrganizationTypeSchema,
  PRIMARY_TYPES,
  PositiveIntegerSchema,
  PrefillDataSchema,
  ProcessSchema,
  ProgressMainSchema,
  PublishedWorkflowIdsSchema,
  RESERVED_ENV_VAR_NAMES,
  ResolvedVariableType,
  RichTextDataSchema,
  RowDataSchema,
  SOURCE_INFO_MAP,
  SelectColumnDataSchema,
  SelectedDatabaseSchema,
  SettingsSchema,
  SingleOrArrayOfString,
  SmallProcessSchema,
  SourcesDataSchema,
  StaticType,
  SuggestionSource,
  SynchronizeDataType,
  SynchronizeOptionsType,
  SynchronizeSource,
  TABLE_EDITOR_TYPES,
  TABLE_IMPORTER_TYPES,
  TIBooleanColumnDataSchema,
  TIDateColumnDataSchema,
  TILabelsColumnDataSchema,
  TINumberColumnDataSchema,
  TITextColumnDataSchema,
  TI_CommentDataSchema,
  TI_DataTabDataSchema,
  TI_DateDataSchema,
  TI_FileUploadDataSchema,
  TI_KeyValueDataSchema,
  TI_ListDataSchema,
  TI_NumberDataSchema,
  TI_TextDataSchema,
  TI_WindowDataSchema,
  TO_DATE_VIEW_TYPES,
  TO_DateDataSchema,
  TO_DateViewTypeSchema,
  TO_FILE_VIEW_TYPES,
  TO_FileDataSchema,
  TO_FileViewTypeSchema,
  TO_KEYVALUE_VIEW_TYPES,
  TO_KeyValueDataSchema,
  TO_KeyValueViewTypeSchema,
  TO_LIST_VIEW_TYPES,
  TO_ListDataSchema,
  TO_ListViewTypeSchema,
  TO_NUMBER_VIEW_TYPES,
  TO_NumberDataSchema,
  TO_NumberIconSchema,
  TO_NumberViewTypeSchema,
  TO_TEXT_VIEW_TYPES,
  TO_TextDataSchema,
  TO_TextIconSchema,
  TO_TextViewTypeSchema,
  TO_USER_VIEW_TYPES,
  TO_UserDataSchema,
  TO_UserViewTypeSchema,
  TRACE_INFO_TYPES,
  TRACE_OVERVIEW_TYPES,
  TableEditorDTOSchema,
  TableEditorSchema,
  TableEditorSchemas,
  TableImporterDTOSchema,
  TableImporterSchema,
  TableImporterSchemas,
  TemplateVariablesSchema,
  TextColumnDataSchema,
  TextFieldDataSchema,
  TextInputSchema,
  TextMainSchema,
  TextSchema,
  TitleInputSchema,
  TraceInfoSchemas,
  TraceKeyMode,
  TraceKeySchema,
  TraceOverviewSchemas,
  TraceWidthSchema,
  TransitionSchema,
  UpdateCodeDTOSchema,
  UpdateDatabaseSchema,
  UpdateGenerationSchema,
  UpdateOrganizationLinkSchema,
  UpdateWorkflowSchema,
  UpdateWorkspaceSchema,
  UserSchema,
  VariableInfoSchema,
  WORKSPACE_NAME_MAX_LENGTH,
  WORKSPACE_NAME_MIN_LENGTH,
  WORKSPACE_VERSION_GROUPS,
  WidgetMainSchema,
  WidthList,
  WidthToInfoMap,
  WorkflowMetadataSchema,
  WorkflowSchema,
  WorkspaceSchema,
  WorkspaceVersionSchema,
  bulkUpsertEnvVarOverridesSchema,
  createDtoSchema,
  createEnvVarSchema,
  encodingOptions,
  envVarNameSchema,
  envVarOverrideSchema,
  envVarTypeSchema,
  envVarValueSchema,
  genRepoSchema,
  hasElevatedRights,
  isBpmnTask,
  processEnvVarSchema,
  syncedDataSchema,
  synchronizeOptionsSchema,
  updateEnvVarSchema,
  upsertEnvVarOverrideSchema,
  userInfoSchema,
  workspaceActivationSchema,
  workspaceNameSchema,
  workspaceSettingsFormSchema,
  workspaceVersionFieldSchema
};
//! bad
//# sourceMappingURL=index.mjs.map